import React, { useState, useEffect, useRef } from "react";

export const legalCss = `
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  :root {
    --teal: #074F4F;
    --gold: #C6A769;
    --cream: #FAF7F2;
    --cream-d: #F0EBE3;
    --white: #fff;
    --text: #2d2d2d;
    --text-l: #8a8a8a;
    --serif: 'Cormorant', Georgia, serif;
    --sans: 'Inter', sans-serif;
    --ease: cubic-bezier(0.16, 1, 0.3, 1);
  }
  .lg-root {
    font-family: var(--sans); background: var(--cream); color: var(--text);
    overflow-x: hidden; -webkit-font-smoothing: antialiased;
  }
  .lg-root a { text-decoration: none; }
  a { color: inherit; }
  .lg-root img { display: block; max-width: 100%; }
  .lg-root ::selection { background: var(--cream-d); color: var(--teal); }

  /* PRELOADER */
  .lg-pre {
    position: fixed; inset: 0; background: var(--cream); z-index: 10000;
    display: flex; align-items: center; justify-content: center; flex-direction: column;
    transition: opacity 1s ease 0.2s, visibility 1s ease 0.2s;
  }
  .lg-pre.done { opacity: 0; visibility: hidden; pointer-events: none; }
  .lg-pre-word {
    font-family: var(--serif); font-size: 32px; font-weight: 300; letter-spacing: 18px;
    text-transform: uppercase; color: var(--teal); opacity: 0;
    animation: lgPreIn 1s 0.3s forwards; padding-left: 18px;
  }
  .lg-pre-line {
    width: 100%; max-width: 180px; height: 1px; background: var(--gold);
    margin-top: 24px; transform: scaleX(0); animation: lgLineIn 1.2s 0.6s forwards;
  }
  @keyframes lgPreIn { from { opacity:0; letter-spacing:36px } to { opacity:1; letter-spacing:18px } }
  @keyframes lgLineIn { to { transform: scaleX(1) } }

  .lg-grain {
    position: fixed; inset: 0; z-index: 9990; pointer-events: none; opacity: 0.02;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size: 128px;
  }

  /* NAV */
  .lg-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    background: rgba(250,247,242,0.92); backdrop-filter: blur(30px); -webkit-backdrop-filter: blur(30px);
  }
  .lg-nav-inner {
    max-width: 1400px; margin: 0 auto; padding: 28px 48px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .lg-nav-logo {
    font-family: var(--serif); font-size: 22px; font-weight: 400;
    letter-spacing: 5px; text-transform: uppercase; color: var(--teal);
  }
  .lg-nav-links { display: flex; gap: 44px; list-style: none; }
  .lg-nav-links a {
    font-size: 12px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase;
    color: var(--text); transition: all 0.4s var(--ease); position: relative; padding-bottom: 4px;
  }
  .lg-nav-links a::after {
    content: ''; position: absolute; bottom: 0; left: 0; width: 100%; height: 1px;
    background: var(--teal); transform: scaleX(0); transform-origin: right; transition: transform 0.4s var(--ease);
  }
  .lg-nav-links a:hover::after { transform: scaleX(1); transform-origin: left; }
  .lg-nav-links a:hover { color: var(--teal); }
  .lg-nav-auth { display: flex; align-items: center; gap: 20px; }
  .lg-nav-login {
    font-size: 11px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: var(--text-l);
  }
  .lg-nav-login:hover { color: var(--gold); }
  .lg-nav-register {
    font-size: 11px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase;
    color: var(--cream); background: var(--teal); padding: 10px 24px;
    transition: all 0.4s var(--ease); position: relative; overflow: hidden;
  }
  .lg-nav-register::before {
    content: ''; position: absolute; inset: 0; background: var(--gold);
    transform: translateY(101%); transition: transform 0.4s var(--ease);
  }
  .lg-nav-register:hover::before { transform: translateY(0); }
  .lg-nav-register span { position: relative; z-index: 1; }
  .lg-nav-register:hover span { color: var(--teal); }
  .lg-nav-toggle {
    display: none; flex-direction: column; justify-content: center; align-items: center;
    gap: 5px; width: 40px; height: 40px; background: none; border: none; cursor: pointer; z-index: 210;
  }
  .lg-nav-toggle span { display: block; width: 24px; height: 1.5px; background: var(--teal); transition: all 0.35s var(--ease); }
  .lg-nav-toggle.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
  .lg-nav-toggle.open span:nth-child(2) { opacity: 0; }
  .lg-nav-toggle.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }
  .lg-nav-mobile {
    display: none; flex-direction: column; background: var(--cream); max-height: 0; overflow: hidden;
    transition: max-height 0.5s var(--ease); box-shadow: 0 12px 30px rgba(7,79,79,0.1);
  }
  .lg-nav-mobile.open { max-height: 600px; }
  .lg-nav-mobile-links { list-style: none; display: flex; flex-direction: column; padding: 12px 24px; }
  .lg-nav-mobile-links li { border-bottom: 1px solid rgba(7,79,79,0.06); }
  .lg-nav-mobile-links li:last-child { border-bottom: none; }
  .lg-nav-mobile-links a {
    display: block; padding: 16px 4px; font-size: 14px; font-weight: 500;
    letter-spacing: 1px; text-transform: uppercase; color: var(--text);
  }
  .lg-nav-mobile-auth { display: flex; flex-direction: column; gap: 12px; padding: 16px 24px 28px; }
  .lg-nav-mobile-auth .lg-nav-login { text-align: center; padding: 10px; }
  .lg-nav-mobile-auth .lg-nav-register { text-align: center; }
  .lg-nav-mobile-scrim {
    position: fixed; inset: 0; z-index: 90; background: rgba(7,79,79,0.3);
    opacity: 0; visibility: hidden; transition: all 0.4s var(--ease);
  }
  .lg-nav-mobile-scrim.open { opacity: 1; visibility: visible; }
  .lg-nav-user-wrap { position: relative; }
  .lg-nav-user {
    display: flex; align-items: center; gap: 12px; font-size: 12px; font-weight: 500;
    letter-spacing: 1px; color: var(--teal); cursor: pointer; user-select: none;
  }
  .lg-nav-user-name { font-size: 12px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
  .lg-nav-user-avatar {
    width: 36px; height: 36px; border-radius: 50%; border: 1.5px solid var(--teal);
    display: flex; align-items: center; justify-content: center; font-family: var(--serif);
    font-size: 14px; color: var(--teal); background: rgba(7,79,79,0.05); transition: all 0.4s var(--ease);
  }
  .lg-nav-user:hover .lg-nav-user-avatar { background: var(--teal); color: var(--cream); }
  .lg-nav-user-chevron { width: 10px; height: 10px; opacity: 0.5; transition: all 0.3s var(--ease); }
  .lg-nav-user-wrap.open .lg-nav-user-chevron { transform: rotate(180deg); opacity: 0.8; }
  .lg-nav-dropdown {
    position: absolute; top: calc(100% + 16px); right: 0; min-width: 220px; background: var(--white);
    box-shadow: 0 4px 24px rgba(7,79,79,0.08), 0 1px 3px rgba(7,79,79,0.04);
    opacity: 0; visibility: hidden; transform: translateY(-8px); transition: all 0.35s var(--ease);
    z-index: 200; overflow: hidden;
  }
  .lg-nav-dropdown::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: var(--gold); }
  .lg-nav-user-wrap.open .lg-nav-dropdown { opacity: 1; visibility: visible; transform: translateY(0); }
  .lg-nav-dd-header { padding: 20px 24px 16px; border-bottom: 1px solid rgba(7,79,79,0.06); }
  .lg-nav-dd-name { font-family: var(--serif); font-size: 18px; color: var(--teal); margin-bottom: 2px; }
  .lg-nav-dd-email { font-size: 11px; color: var(--text-l); font-weight: 300; }
  .lg-nav-dd-items { padding: 8px 0; }
  .lg-nav-dd-item {
    display: flex; align-items: center; gap: 12px; padding: 12px 24px; font-size: 12px;
    letter-spacing: 0.5px; color: var(--text); cursor: pointer; transition: all 0.3s var(--ease);
    border: none; background: none; width: 100%; text-align: left;
  }
  .lg-nav-dd-item:hover { background: var(--cream); color: var(--teal); }
  .lg-nav-dd-item svg { width: 16px; height: 16px; opacity: 0.4; transition: opacity 0.3s; }
  .lg-nav-dd-item:hover svg { opacity: 0.7; }
  .lg-nav-dd-divider { height: 1px; background: rgba(7,79,79,0.06); }
  .lg-nav-dd-item.logout { color: #b33; }
  .lg-nav-dd-item.logout:hover { background: rgba(187,51,51,0.04); color: #b33; }

  /* HERO */
  .lg-hero { padding: 190px 48px 70px; text-align: center; position: relative; overflow: hidden; }
  .lg-hero::before {
    content: ''; position: absolute; top: -40%; left: 50%; transform: translateX(-50%);
    width: 80vw; height: 80vw; max-width: 900px; max-height: 900px; border-radius: 50%;
    background: radial-gradient(circle, rgba(198,167,105,0.08) 0%, transparent 62%); pointer-events: none;
  }
  .lg-label { font-size: 11px; font-weight: 500; letter-spacing: 3px; text-transform: uppercase; color: var(--gold); margin-bottom: 16px; position: relative; z-index: 1; }
  .lg-hero h1 {
    font-family: var(--serif); font-size: clamp(34px, 4.6vw, 56px); font-weight: 300; color: var(--teal);
    line-height: 1.1; margin-bottom: 14px; position: relative; z-index: 1;
  }
  .lg-updated { font-size: 12px; color: var(--text-l); position: relative; z-index: 1; }

  /* BODY */
  .lg-body { max-width: 680px; margin: 0 auto; padding: 20px 24px 100px; }
  .lg-intro {
    font-size: 13px; line-height: 1.65; color: var(--text-l); font-weight: 300;
    padding-bottom: 28px; margin-bottom: 28px; border-bottom: 1px solid rgba(7,79,79,0.08);
  }
  .lg-intro strong { color: var(--teal); font-weight: 500; }
  .lg-sec { margin-bottom: 26px; }
  .lg-sec-num {
    font-family: var(--serif); font-size: 12px; color: var(--gold); letter-spacing: 1px; margin-bottom: 4px;
  }
  .lg-sec h2 {
    font-family: var(--serif); font-size: 19px; font-weight: 400; color: var(--teal); margin-bottom: 8px;
  }
  .lg-sec p { font-size: 13px; line-height: 1.55; color: var(--text); font-weight: 300; margin-bottom: 8px; }
  .lg-sec p:last-child { margin-bottom: 0; }
  .lg-sec ul { list-style: none; margin: 8px 0; }
  .lg-sec li {
    font-size: 13px; line-height: 1.5; color: var(--text); font-weight: 300;
    padding-left: 18px; position: relative; margin-bottom: 4px;
  }
  .lg-sec li::before {
    content: ''; position: absolute; left: 0; top: 7px; width: 4px; height: 4px;
    background: var(--gold); border-radius: 50%;
  }
  .lg-sec a { color: var(--teal); border-bottom: 1px solid var(--gold); transition: color 0.3s; }
  .lg-sec a:hover { color: var(--gold); }
  .lg-contact-box {
    background: var(--white); border-left: 2px solid var(--gold); padding: 18px 22px; margin-top: 32px;
  }
  .lg-contact-box p { margin-bottom: 4px; font-size: 13px; }
  .lg-contact-box p:last-child { margin-bottom: 0; }
  .lg-contact-box strong { color: var(--teal); }

  /* FOOTER */
  .lg-footer { background: var(--teal); padding: 80px 48px 32px; }
  .lg-f-inner { max-width: 1200px; margin: 0 auto; }
  .lg-f-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 56px; }
  .lg-f-brand-name { font-family: var(--serif); font-size: 22px; font-weight: 400; letter-spacing: 5px; text-transform: uppercase; color: var(--cream); margin-bottom: 14px; }
  .lg-f-brand p { font-size: 13px; line-height: 1.7; color: rgba(250,247,242,0.4); max-width: 260px; }
  .lg-f-social { display: flex; gap: 22px; margin-top: 24px; }
  .lg-f-social a {
    width: 36px; height: 36px; border: 1px solid rgba(198,167,105,0.25); border-radius: 50%;
    display: flex; align-items: center; justify-content: center; font-size: 11px;
    letter-spacing: 1px; text-transform: uppercase; color: rgba(250,247,242,0.5); transition: all 0.3s;
  }
  .lg-f-social a:hover { background: var(--gold); border-color: var(--gold); color: var(--teal); }
  .lg-f-col h5 { font-size: 10px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: var(--gold); margin-bottom: 20px; }
  .lg-f-col ul { list-style: none; }
  .lg-f-col li { margin-bottom: 12px; }
  .lg-f-col a { font-size: 13px; color: rgba(250,247,242,0.4); transition: color 0.3s; }
  .lg-f-col a:hover { color: var(--gold); }
  .lg-f-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(198,167,105,0.2), transparent); margin-bottom: 32px; }
  .lg-f-bottom { display: flex; justify-content: space-between; font-size: 11px; color: rgba(250,247,242,0.25); }
  .lg-f-bottom a { color: rgba(250,247,242,0.25); margin-left: 20px; transition: color 0.3s; }
  .lg-f-bottom a:hover { color: var(--gold); }

  @media (max-width: 1024px) {
    .lg-nav-links, .lg-nav-auth { display: none; }
    .lg-nav-toggle { display: flex; }
    .lg-nav-mobile { display: flex; }
    .lg-hero { padding: 150px 24px 56px; }
    .lg-f-grid { grid-template-columns: 1fr 1fr; gap: 40px 24px; }
    .lg-f-brand { grid-column: 1 / -1; }
  }
  @media (max-width: 640px) {
    .lg-nav-inner { padding: 20px 20px; }
    .lg-hero { padding: 130px 18px 44px; }
    .lg-body { padding: 12px 20px 90px; }
    .lg-f-grid { grid-template-columns: 1fr; gap: 32px; }
    .lg-f-bottom { flex-direction: column; gap: 10px; text-align: center; }
    .lg-f-bottom a { margin-left: 10px; }
  }
`;

export function useSiteChrome() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [preDone, setPreDone] = useState(false);
  const userWrapRef = useRef(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;

  useEffect(() => {
    setAuthed(!!token);
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => setPreDone(true), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function handleOutside(e) {
      if (userWrapRef.current && !userWrapRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, []);

  let displayFirst = (typeof window !== "undefined" && localStorage.getItem("user_first_name")) || "";
  let displayLast = (typeof window !== "undefined" && localStorage.getItem("user_last_name")) || "";
  let displayEmail = "";
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      displayEmail = payload.email || "";
    } catch (e) {}
  }
  if (!displayFirst && displayEmail) displayFirst = displayEmail.split("@")[0];
  const initials = ((displayFirst ? displayFirst.charAt(0) : "") + (displayLast ? displayLast.charAt(0) : "")).toUpperCase() || "?";

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_first_name");
    localStorage.removeItem("user_last_name");
    window.location.reload();
  };

  return { mobileOpen, setMobileOpen, userMenuOpen, setUserMenuOpen, authed, preDone, userWrapRef, displayFirst, displayLast, displayEmail, initials, handleLogout };
}

export function SiteNav() {
  const { mobileOpen, setMobileOpen, userMenuOpen, setUserMenuOpen, authed, userWrapRef, displayFirst, displayLast, displayEmail, initials, handleLogout } = useSiteChrome();
  return (
    <>
      <nav className="lg-nav">
        <div className="lg-nav-inner">
          <a href="/" className="lg-nav-logo">Aware</a>
          <ul className="lg-nav-links">
            <li><a href="/">Acasă</a></li>
            <li><a href="/despre-noi">Despre noi</a></li>
            <li><a href="/servicii">Servicii</a></li>
            <li><a href="/evenimente">Evenimente</a></li>
          </ul>
          {!authed ? (
            <div className="lg-nav-auth">
              <a href="/login" className="lg-nav-login">Autentificare</a>
              <a href="/register" className="lg-nav-register"><span>Înregistrare</span></a>
            </div>
          ) : (
            <div className={`lg-nav-user-wrap${userMenuOpen ? " open" : ""}`} ref={userWrapRef}>
              <div className="lg-nav-user" onClick={() => setUserMenuOpen((o) => !o)}>
                <span className="lg-nav-user-name">{displayFirst}</span>
                <div className="lg-nav-user-avatar">{initials}</div>
                <svg className="lg-nav-user-chevron" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1l4 4 4-4"/></svg>
              </div>
              <div className="lg-nav-dropdown">
                <div className="lg-nav-dd-header">
                  <div className="lg-nav-dd-name">{displayFirst}{displayLast ? " " + displayLast : ""}</div>
                  <div className="lg-nav-dd-email">{displayEmail}</div>
                </div>
                <div className="lg-nav-dd-items">
                  <button className="lg-nav-dd-item" onClick={() => (window.location.href = "/profil")}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    Profilul meu
                  </button>
                  <div className="lg-nav-dd-divider" />
                  <button className="lg-nav-dd-item logout" onClick={handleLogout}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                    Deconectare
                  </button>
                </div>
              </div>
            </div>
          )}
          <button className={`lg-nav-toggle${mobileOpen ? " open" : ""}`} onClick={() => setMobileOpen((o) => !o)} aria-label="Meniu">
            <span></span><span></span><span></span>
          </button>
        </div>
        <div className={`lg-nav-mobile${mobileOpen ? " open" : ""}`}>
          <ul className="lg-nav-mobile-links">
            <li><a href="/">Acasă</a></li>
            <li><a href="/despre-noi">Despre noi</a></li>
            <li><a href="/servicii">Servicii</a></li>
            <li><a href="/evenimente">Evenimente</a></li>
          </ul>
          {!authed && (
            <div className="lg-nav-mobile-auth">
              <a href="/login" className="lg-nav-login">Autentificare</a>
              <a href="/register" className="lg-nav-register"><span>Înregistrare</span></a>
            </div>
          )}
        </div>
      </nav>
      <div className={`lg-nav-mobile-scrim${mobileOpen ? " open" : ""}`} onClick={() => setMobileOpen(false)} />
    </>
  );
}

export function SiteFooter() {
  return (
    <footer className="lg-footer">
      <div className="lg-f-inner">
        <div className="lg-f-grid">
          <div className="lg-f-brand">
            <div className="lg-f-brand-name">Aware</div>
            <p>Conștientizarea este începutul schimbării. Servicii psihologice bazate pe profesionalism, responsabilitate și onestitate.</p>
            <div className="lg-f-social">
              <a href="https://www.instagram.com/_aware.psychotherapy_?igsi=MWl3eXg5aTl0ZjdsZg==" target="_blank" rel="noopener noreferrer">Ig</a>
              <a href="https://www.facebook.com/share/1Bt6W3XQ41/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">Fb</a>
            </div>
          </div>
          <div className="lg-f-col">
            <h5>Servicii</h5>
            <ul>
              <li><a href="/servicii">Evaluare Psihologică Clinică</a></li>
              <li><a href="/servicii">Psihoterapie Individuală</a></li>
              <li><a href="/servicii">Psihoterapie de Cuplu și Familie</a></li>
              <li><a href="/servicii">Consiliere Psihologică</a></li>
            </ul>
          </div>
          <div className="lg-f-col">
            <h5>Specialiști</h5>
            <ul>
              <li><a href="/alexandra">Alexandra Strîmbeanu</a></li>
              <li><a href="/alina">Alina Gheorghe</a></li>
              <li><a href="/despre-noi">Florena Antonescu</a></li>
              <li><a href="/manuela">Manuela Capalb</a></li>
            </ul>
          </div>
          <div className="lg-f-col">
            <h5>Aware</h5>
            <ul>
              <li><a href="/">Acasă</a></li>
              <li><a href="/despre-noi">Despre noi</a></li>
              <li><a href="/#contact">Contact</a></li>
              <li><a href="/evenimente">Evenimente</a></li>
            </ul>
          </div>
        </div>
        <div className="lg-f-divider" />
        <div className="lg-f-bottom">
          <span>© 2025 Aware — Centru de Psihoterapie</span>
          <div>
            <a href="/conditii">Termeni</a>
            <a href="/confidentialitate">Confidențialitate</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function LegalPreloader() {
  const { preDone } = useSiteChrome();
  return (
    <div className={`lg-pre${preDone ? " done" : ""}`}>
      <div className="lg-pre-word">Aware</div>
      <div className="lg-pre-line" />
    </div>
  );
}
